import { WebAppState } from '../realtime';
import type { LiveVideoSource } from '../realtime';
import type { CallerVideoConnectionState, CameraState, RecordingState } from './videoSignalling';

export type VideoCallState = {
  callerConnectionState: CallerVideoConnectionState;
  recordingState: RecordingState;
  cameraState: CameraState;
  torchAvailable: boolean;
  torchOn: boolean;
  webAppState: WebAppState;
  showRecording: boolean;
  activeProducerId: string | null;
  consumedProducerId: string | null;
  transportRecoveryInProgress: boolean;
  lastCameraSwitchFailedAt: number | null;
  lastTorchToggleFailedAt: number | null;
};

export const INITIAL_VIDEO_CALL_STATE: VideoCallState = {
  callerConnectionState: 'Connecting',
  recordingState: 'NotReady',
  cameraState: 'Enabled',
  torchAvailable: false,
  torchOn: false,
  webAppState: WebAppState.None,
  showRecording: false,
  activeProducerId: null,
  consumedProducerId: null,
  transportRecoveryInProgress: false,
  lastCameraSwitchFailedAt: null,
  lastTorchToggleFailedAt: null,
};

export type VideoCallEvent =
  | {
      type: 'STATES_LOADED';
      callerConnectionState: CallerVideoConnectionState;
      recordingState: RecordingState;
      cameraState: CameraState;
      torchAvailable: boolean;
      torchOn: boolean;
      webAppState: WebAppState;
    }
  | { type: 'JOIN_ROOM_SUCCEEDED'; activeProducerId: string | null }
  | { type: 'NEW_PRODUCER_AVAILABLE'; producerId: string }
  | { type: 'PRODUCER_STOPPED'; stoppedProducerId: string }
  | { type: 'CALLER_VIDEO_CONNECTION_STATE_CHANGED'; state: CallerVideoConnectionState }
  | { type: 'RECORDING_STATE_CHANGED'; state: RecordingState }
  | { type: 'CAMERA_STATE_CHANGED'; state: CameraState }
  | { type: 'TORCH_STATE_CHANGED'; available: boolean; on: boolean }
  | { type: 'CAMERA_SWITCH_FAILED'; at: number }
  | { type: 'TORCH_TOGGLE_FAILED'; at: number }
  | { type: 'WEB_APP_STATE_CHANGED'; state: WebAppState }
  | { type: 'CONSUME_SUCCEEDED'; producerId: string }
  | { type: 'CONSUME_ATTEMPT_FAILED' }
  | { type: 'TRANSPORT_RECOVERY_STARTED' }
  | { type: 'TRANSPORT_RECOVERY_FINISHED' }
  | { type: 'REWIND_TO_RECORDING' }
  | { type: 'RESUME_LIVE_STREAM' };

export function isStaleProducerStop(state: VideoCallState, stoppedProducerId: string): boolean {
  if (state.activeProducerId !== null) {
    return state.activeProducerId !== stoppedProducerId;
  }
  return state.consumedProducerId !== null && state.consumedProducerId !== stoppedProducerId;
}

export function videoCallReducer(state: VideoCallState, event: VideoCallEvent): VideoCallState {
  switch (event.type) {
    case 'STATES_LOADED':
      return {
        ...state,
        callerConnectionState: event.callerConnectionState,
        recordingState: event.recordingState,
        cameraState: event.cameraState,
        torchAvailable: event.torchAvailable,
        torchOn: event.torchAvailable && event.torchOn,
        webAppState: event.webAppState,
      };

    case 'JOIN_ROOM_SUCCEEDED':
      return { ...state, activeProducerId: event.activeProducerId };

    case 'NEW_PRODUCER_AVAILABLE':
      return { ...state, activeProducerId: event.producerId };

    case 'PRODUCER_STOPPED':
      if (isStaleProducerStop(state, event.stoppedProducerId)) {
        return state;
      }
      return {
        ...state,
        activeProducerId: null,
        consumedProducerId: null,
        callerConnectionState: 'Disconnected',
      };

    case 'CALLER_VIDEO_CONNECTION_STATE_CHANGED':
      return { ...state, callerConnectionState: event.state };

    case 'RECORDING_STATE_CHANGED':
      return { ...state, recordingState: event.state };

    case 'CAMERA_STATE_CHANGED':
      return { ...state, cameraState: event.state };

    case 'TORCH_STATE_CHANGED':
      return { ...state, torchAvailable: event.available, torchOn: event.available && event.on };

    case 'CAMERA_SWITCH_FAILED':
      return { ...state, lastCameraSwitchFailedAt: event.at };

    case 'TORCH_TOGGLE_FAILED':
      return { ...state, lastTorchToggleFailedAt: event.at };

    case 'WEB_APP_STATE_CHANGED':
      return { ...state, webAppState: event.state };

    case 'CONSUME_SUCCEEDED':
      return { ...state, consumedProducerId: event.producerId };

    case 'CONSUME_ATTEMPT_FAILED':
      return { ...state, consumedProducerId: null };

    case 'TRANSPORT_RECOVERY_STARTED':
      if (state.transportRecoveryInProgress) {
        return state;
      }
      return { ...state, transportRecoveryInProgress: true, consumedProducerId: null };

    case 'TRANSPORT_RECOVERY_FINISHED':
      return { ...state, transportRecoveryInProgress: false };

    case 'REWIND_TO_RECORDING':
      return { ...state, showRecording: true };

    case 'RESUME_LIVE_STREAM':
      return { ...state, showRecording: false };

  }
}

export type DisplayPanelMode =
  | 'RecordingPlayback'
  | 'FatalError'
  | 'Disconnected'
  | 'Connecting'
  | 'CameraTurnedOff'
  | 'AppleEedInstructions'
  | 'GoogleElsInstructions'
  | 'LiveVideo'
  | 'WebAppState';

export function isVideoErrorState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.CameraPermissionRejected ||
    webAppState === WebAppState.UnsupportedBrowser ||
    webAppState === WebAppState.VideoTransmissionFailed
  );
}

export function getDisplayPanelMode(state: VideoCallState, source: LiveVideoSource): DisplayPanelMode {
  if (state.showRecording) return 'RecordingPlayback';

  if (isVideoErrorState(state.webAppState)) return 'FatalError';

  if (source === 'WebApp' && (
    state.webAppState === WebAppState.LocationPermissionRequested ||
    state.webAppState === WebAppState.CameraPermissionRequested
  )) return 'WebAppState';

  if (state.callerConnectionState === 'Disconnected') return 'Disconnected';

  if (
    state.callerConnectionState === 'Connected' &&
    state.cameraState !== 'Enabled'
  ) {
    return 'CameraTurnedOff';
  }

  if (state.callerConnectionState === 'Connecting') return 'Connecting';

  if (state.callerConnectionState === 'NotConnected') {
    if (source === 'AppleEed') return 'AppleEedInstructions';
    if (source === 'GoogleEls') return 'GoogleElsInstructions';
    return 'WebAppState';
  }

  if (state.callerConnectionState === 'Connected') return 'LiveVideo';

  return 'WebAppState';
}

export function getWebAppStateIcon(): string {
  return 'Images/Incident/video-loading.svg';
}

export function getWebAppStateTextKey(webAppState: WebAppState): string {
  switch (webAppState) {
    case WebAppState.None:
    case WebAppState.WebAppSmsSent:
      return 'WebAppState_WaitingForLinkOpen';
    case WebAppState.AppOpened:
      return 'WebAppState_AppOpened';
    case WebAppState.CameraPermissionRequested:
      return 'WebAppState_CameraPermissionRequested';
    case WebAppState.CameraPermissionGranted:
      return 'WebAppState_CameraPermissionGranted';
    case WebAppState.CameraPermissionRejected:
      return 'WebAppState_CameraPermissionRejected';
    case WebAppState.UnsupportedBrowser:
      return 'WebAppState_UnsupportedBrowser';
    case WebAppState.StartingVideoTransmission:
      return 'WebAppState_StartingVideoTransmission';
    case WebAppState.VideoTransmissionActive:
      return 'WebAppState_VideoTransmissionActive';
    case WebAppState.VideoTransmissionFailed:
      return 'WebAppState_VideoTransmissionFailed';
    case WebAppState.LocationSmsSent:
      return 'WebAppState_Location_WaitingForLinkOpen';
    case WebAppState.LocationPermissionRequested:
      return 'WebAppState_Location_PermissionRequested';
    case WebAppState.LocationPermissionGranted:
      return 'WebAppState_Location_PermissionGranted';
    case WebAppState.LocationPermissionDenied:
      return 'WebAppState_Location_PermissionDenied';
    case WebAppState.LocationUnavailableNotSupported:
      return 'WebAppState_Location_Unsupported';
    case WebAppState.LocationPositionUnavailable:
      return 'WebAppState_Location_PositionUnavailable';
    case WebAppState.LocationTimeout:
      return 'WebAppState_Location_Timeout';
    default:
      return 'WebAppState_WaitingForLinkOpen';
  }
}

export function getWebAppStateDescriptionKey(webAppState: WebAppState): string | null {
  switch (webAppState) {
    case WebAppState.None:
    case WebAppState.WebAppSmsSent:
      return 'WebAppState_WaitingForLinkOpen_Description';
    case WebAppState.CameraPermissionRejected:
      return 'WebAppState_CameraPermissionRejected_Description';
    case WebAppState.UnsupportedBrowser:
      return 'WebAppState_UnsupportedBrowser_Description';
    case WebAppState.StartingVideoTransmission:
      return 'WebAppState_StartingVideoTransmission_Description';
    case WebAppState.VideoTransmissionFailed:
      return 'WebAppState_VideoTransmissionFailed_Description';
    case WebAppState.LocationSmsSent:
      return 'WebAppState_Location_WaitingForLinkOpen_Description';
    case WebAppState.LocationPermissionRequested:
      return 'WebAppState_Location_PermissionRequested_Description';
    case WebAppState.LocationPermissionGranted:
      return 'WebAppState_Location_PermissionGranted_Description';
    case WebAppState.LocationPermissionDenied:
      return 'WebAppState_Location_PermissionDenied_Description';
    case WebAppState.LocationUnavailableNotSupported:
      return 'WebAppState_Location_Unsupported_Description';
    case WebAppState.LocationPositionUnavailable:
      return 'WebAppState_Location_PositionUnavailable_Description';
    case WebAppState.LocationTimeout:
      return 'WebAppState_Location_Timeout_Description';
    default:
      return null;
  }
}

export function isRewindDisabled(state: VideoCallState): boolean {
  return state.recordingState === 'NotReady';
}

export function canSwapCameraOrTakeSnapshot(state: VideoCallState): boolean {
  return (
    state.callerConnectionState === 'Connected' &&
    state.cameraState === 'Enabled'
  );
}

export function canToggleTorch(state: VideoCallState): boolean {
  return canSwapCameraOrTakeSnapshot(state) && state.torchAvailable;
}

export function showLiveVideo(
  showRecording: boolean,
  connectionState: CallerVideoConnectionState | null,
  cameraState: CameraState,
): boolean {
  return (
    !showRecording &&
    connectionState === 'Connected' &&
    cameraState === 'Enabled'
  );
}

export const CALLER_VIDEO_CONNECTION_STATE_NAMES: Record<CallerVideoConnectionState, string> = {
  NotConnected: 'NotConnected',
  Connecting: 'Connecting',
  Connected: 'Connected',
  Disconnected: 'Disconnected',
};

export const RECORDING_STATE_NAMES: Record<RecordingState, string> = {
  NotReady: 'NotReady',
  Ready: 'Ready',
};

export const CAMERA_STATE_NAMES: Record<CameraState, string> = {
  Enabled: 'Enabled',
  Disabled: 'Disabled',
};
